import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Edit as EditIcon, Send as SendIcon } from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiService from "../services/api";
import type { AdminMetricsReportScheduleDto } from "../services/metricsReportAdminTypes";
import {
  METRICS_REPORT_WEEKDAYS,
  type MetricsReportScheduleUpsertPayload,
} from "../miContenido/metricsReportSchedule";
import { metricsReportPeriodOption } from "../miContenido/metricsReportPeriod";
import {
  MetricsReportEmailForm,
  scheduleToFormValues,
} from "../miContenido/programStats/metricsDetail/MetricsReportEmailForm";
import { useAuth } from "../contexts/AuthContext";

function weekdayLabel(day: number): string {
  return METRICS_REPORT_WEEKDAYS.find((d) => d.value === day)?.label ?? `Día ${day}`;
}

function channelLabel(row: AdminMetricsReportScheduleDto): string {
  if (row.scope === "program" && row.programTitle) {
    return `${row.podcastTitle} — ${row.programTitle}`;
  }
  return row.podcastTitle;
}

const AdminMetricsReports: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sendTarget, setSendTarget] = useState<AdminMetricsReportScheduleDto | null>(null);
  const [editTarget, setEditTarget] = useState<AdminMetricsReportScheduleDto | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    severity: "success" | "error";
    message: string;
  } | null>(null);

  const schedulesQuery = useQuery({
    queryKey: [
      "admin-metrics-reports",
      search,
      enabledOnly,
      page,
      rowsPerPage,
    ],
    queryFn: () =>
      apiService.getAdminMetricsReportSchedules({
        q: search.trim() || undefined,
        enabled: enabledOnly ? true : undefined,
        page: page + 1,
        limit: rowsPerPage,
      }),
    enabled: user?.role === "admin",
  });

  const patchMutation = useMutation({
    mutationFn: (args: { scheduleId: string; enabled: boolean }) =>
      apiService.patchAdminMetricsReportSchedule(args.scheduleId, {
        enabled: args.enabled,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-metrics-reports"] });
    },
    onError: (err: unknown) => {
      setFeedback({
        severity: "error",
        message:
          err instanceof Error ? err.message : "No se pudo actualizar el schedule",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: (args: {
      scheduleId: string;
      payload: MetricsReportScheduleUpsertPayload;
    }) =>
      apiService.patchAdminMetricsReportSchedule(args.scheduleId, args.payload),
    onSuccess: () => {
      setEditTarget(null);
      setEditError(null);
      setFeedback({
        severity: "success",
        message: "Configuración de envío guardada.",
      });
      void queryClient.invalidateQueries({ queryKey: ["admin-metrics-reports"] });
    },
    onError: (err: unknown) => {
      setEditError(
        err instanceof Error ? err.message : "No se pudo guardar la configuración",
      );
    },
  });

  const sendMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      apiService.sendAdminMetricsReportNow(scheduleId),
    onSuccess: () => {
      setSendTarget(null);
      setFeedback({
        severity: "success",
        message: "Envío iniciado correctamente.",
      });
      void queryClient.invalidateQueries({ queryKey: ["admin-metrics-reports"] });
    },
    onError: (err: unknown) => {
      setFeedback({
        severity: "error",
        message: err instanceof Error ? err.message : "Error al enviar el reporte",
      });
    },
  });

  const rows = schedulesQuery.data?.schedules ?? [];
  const total = schedulesQuery.data?.total ?? 0;

  const busyId = useMemo(() => {
    if (editMutation.isPending && editMutation.variables) {
      return editMutation.variables.scheduleId;
    }
    if (patchMutation.isPending && patchMutation.variables) {
      return patchMutation.variables.scheduleId;
    }
    if (sendMutation.isPending && sendMutation.variables) {
      return sendMutation.variables;
    }
    return null;
  }, [
    editMutation.isPending,
    editMutation.variables,
    patchMutation.isPending,
    patchMutation.variables,
    sendMutation.isPending,
    sendMutation.variables,
  ]);

  if (user?.role !== "admin") {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">Solo administradores pueden ver esta página.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h1" gutterBottom>
            Reportes de métricas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Schedules de envío automático por empresa. Edita destinatarios, día y período, o envía manualmente.
          </Typography>
        </Box>

        {feedback ? (
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
          >
            {feedback.message}
          </Alert>
        ) : null}

        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
          >
            <TextField
              size="small"
              label="Buscar"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 220, flex: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={enabledOnly}
                  onChange={(e) => {
                    setEnabledOnly(e.target.checked);
                    setPage(0);
                  }}
                />
              }
              label="Solo activos"
            />
          </Stack>
        </Paper>

        <Paper>
          {schedulesQuery.isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : schedulesQuery.isError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              No se pudieron cargar los schedules.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Empresa</TableCell>
                      <TableCell>Canal / Programa</TableCell>
                      <TableCell>Destinatarios</TableCell>
                      <TableCell>Día</TableCell>
                      <TableCell>Período</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Último envío</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No hay schedules que coincidan con el filtro.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.companyName}</TableCell>
                          <TableCell>{channelLabel(row)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap title={row.recipients.join(", ")}>
                              {row.recipients.join(", ") || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{weekdayLabel(row.dayOfWeek)}</TableCell>
                          <TableCell>
                            {metricsReportPeriodOption(row.reportPeriod).label}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.enabled ? "Activo" : "Inactivo"}
                              color={row.enabled ? "success" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            {row.lastDelivery ? (
                              <Typography variant="body2">
                                {row.lastDelivery.sendDateYmd}
                                <br />
                                {row.lastDelivery.sentCount} enviados
                                {row.lastDelivery.failedCount > 0
                                  ? ` · ${row.lastDelivery.failedCount} fallidos`
                                  : ""}
                              </Typography>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              <IconButton
                                size="small"
                                aria-label="Editar configuración"
                                disabled={busyId === row.id}
                                onClick={() => {
                                  setEditError(null);
                                  setEditTarget(row);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                  busyId === row.id && sendMutation.isPending ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <SendIcon fontSize="small" />
                                  )
                                }
                                disabled={!row.enabled || row.recipients.length === 0 || busyId === row.id}
                                onClick={() => setSendTarget(row)}
                              >
                                Enviar ahora
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                disabled={busyId === row.id}
                                onClick={() =>
                                  patchMutation.mutate({
                                    scheduleId: row.id,
                                    enabled: !row.enabled,
                                  })
                                }
                              >
                                {row.enabled ? "Desactivar" : "Activar"}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Filas"
              />
            </>
          )}
        </Paper>
      </Stack>

      <Drawer
        anchor="right"
        open={Boolean(editTarget)}
        onClose={() => {
          if (!editMutation.isPending) {
            setEditTarget(null);
            setEditError(null);
          }
        }}
        PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, p: 3 } }}
      >
        {editTarget ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h2" gutterBottom>
                Configuración de envío
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editTarget.companyName} · {channelLabel(editTarget)}
              </Typography>
            </Box>
            <MetricsReportEmailForm
              key={editTarget.id}
              layout="sidebar"
              initialValues={scheduleToFormValues(
                editTarget,
                editTarget.recipients.join(", "),
              )}
              savedSchedule={editTarget}
              defaultRecipients={editTarget.recipients[0] ?? ""}
              savePending={editMutation.isPending}
              errorMessage={editError}
              onClearError={() => setEditError(null)}
              onSave={(payload: MetricsReportScheduleUpsertPayload) =>
                editMutation.mutate({
                  scheduleId: editTarget.id,
                  payload: {
                    ...payload,
                    contentBrandFilter: editTarget.contentBrandFilter,
                  },
                })
              }
            />
          </Stack>
        ) : null}
      </Drawer>

      <Dialog open={Boolean(sendTarget)} onClose={() => setSendTarget(null)}>
        <DialogTitle>Enviar reporte ahora</DialogTitle>
        <DialogContent>
          {sendTarget ? (
            <Typography variant="body2">
              Se enviará el informe de <strong>{channelLabel(sendTarget)}</strong> (
              {sendTarget.companyName}) a {sendTarget.recipients.length} destinatario(s) con el
              período correspondiente a hoy.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendTarget(null)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!sendTarget || sendMutation.isPending}
            onClick={() => sendTarget && sendMutation.mutate(sendTarget.id)}
          >
            Confirmar envío
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminMetricsReports;
